import { buildUrl, defaultReadHeaders, fetchJson } from "../../utils";
import type { NavigationJourney, TravelRecord } from "./task/travel";

const backend = process.env.REACT_APP_BACKEND_URL || "";
const base = `${backend}/task-execution-locations`;

export type ExecutionLocationPoint = {
  segmentId?: number | null;
  sequence: number;
  currentTaskId: string | null;
  capturedAt: string;
  receivedAt: string;
  latitude: number;
  longitude: number;
  horizontalAccuracyMeters: number;
  speedMps: number | null;
  courseDegrees: number | null;
  clientPhase: string | null;
};

export type RecordedRoutePoint = { latitude: number; longitude: number; capturedAt: string; segmentId?: number | null; sequence?: number };

export function recordedRouteSegments<T extends RecordedRoutePoint>(points: readonly T[]): T[][] {
  const segments: T[][] = [];
  for (const point of points) {
    const previous = segments.at(-1)?.at(-1);
    const gap = previous ? Date.parse(point.capturedAt) - Date.parse(previous.capturedAt) : 0;
    const hasSegments = previous?.segmentId != null && point.segmentId != null;
    if (!previous || (hasSegments ? point.segmentId !== previous.segmentId : gap >= 30_000)) segments.push([]);
    segments[segments.length - 1].push(point);
  }
  return segments;
}

export type ExecutionSession = {
  navigation?: NavigationJourney | null;
  id: string;
  taskId: string;
  currentTaskId: string | null;
  status: string;
};

export type LiveExecution = {
  execution: ExecutionSession;
  locationId?: string | null;
  machineId?: string | null;
  locationName?: string;
  machineName?: string;
  taskType?: string;
  operatorId: string;
  operatorName: string;
  operatorEmail?: string;
  operatorPhotoUrl?: string | null;
  latestPoint: ExecutionLocationPoint | null;
  isStale: boolean;
};

export type ExecutionRoute = {
  taskMetrics?: {
    taskId: string;
    startedAt: string | null;
    doneAt: string | null;
    distanceMiles: number | null;
    durationSeconds: number;
  } | null;
  startAddress?: string;
  execution: ExecutionSession | null;
  operatorId: string | null;
  operatorName: string;
  operatorEmail?: string;
  operatorPhotoUrl?: string | null;
  points: ExecutionLocationPoint[];
  truncated: boolean;
};

const read = () => ({
  method: "GET",
  credentials: "include" as const,
  headers: defaultReadHeaders(),
});

export const fetchLiveExecutions = () =>
  fetchJson<{ items: LiveExecution[]; trips?: TravelRecord[] }>(
    `${base}/live`,
    read(),
    "Failed to load live execution locations",
  );

export const fetchExecutionRoute = (taskId: string, limit = 1_000, scope?: "growth") =>
  fetchJson<ExecutionRoute>(
    buildUrl(scope === "growth" ? `${backend}/growth/task-execution-locations/route` : `${base}/route`, { taskId, limit }),
    read(),
    "Failed to load execution route",
  );


export async function fetchExecutionStartAddress(executionId: string, signal: AbortSignal, scope?: "growth"): Promise<string> {
  const result = await fetchJson<{ address: string }>(buildUrl(
    scope === "growth" ? `${backend}/growth/task-execution-locations/start-address` : `${base}/start-address`, { executionId }),
    { ...read(), signal }, "Could not load starting address");
  return result.address;
}
